import React, { createRef, FC, ReactNode, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { useIntl } from 'react-intl';
import images from '../../../assets/images/images';
import popupStyles from './popup.module.scss';
import { Image } from '../..';
import { cn, isTouchScreen } from '../../../helpers';

interface IPopupProps {
    children: ReactNode;
    headertext?: string;
    onClosePopup?: (event?: React.MouseEvent) => void;
    fullscreen?: boolean;
    screen75?: boolean;
    className?: string;
    show: boolean;
    type?: string;
    skipHeaderTranslation?: boolean;
    multilineHeader?: boolean;
    htmlHeader?: JSX.Element;
}

export const Popup: FC<IPopupProps> = ({
    type,
    show,
    className,
    skipHeaderTranslation,
    headertext,
    fullscreen,
    screen75,
    htmlHeader,
    children,
    multilineHeader,
    onClosePopup,
}) => {
    const innerPopup = createRef<HTMLDivElement>();
    const imageRef = useRef<HTMLImageElement>(null);
    const intl = useIntl();

    const isDismissable = Boolean(onClosePopup);
    const shouldRenderHeader = headertext || htmlHeader;

    const headerClasses = cn(popupStyles.popup_header, 'd-flex justify-content-between align-self-start');
    const innerClasses = cn(popupStyles.popup_inner, popupStyles.normal, 'd-flex flex-column', {
        [popupStyles.fullscreen]: fullscreen,
        [popupStyles.screen75]: screen75,
        [popupStyles.typed]: type,
        [popupStyles[type]]: type,
    });

    useEffect(() => {
        if (imageRef.current != null) {
            imageRef.current.focus();
        }
    }, [imageRef]);

    const renderPopupHeaderIcon = (): JSX.Element | null => {
        switch (type) {
            case 'alert':
                return <Image src={images.alert} alt="ALERT_ICON" className={popupStyles.typeIcon} />;
            case 'info':
                return <Image src={images.checkmark} alt="INFORMATION" className={popupStyles.typeIcon} />;
            default:
                return null;
        }
    };

    const renderPopupHeaderText = (): string => {
        return skipHeaderTranslation ? headertext : intl.formatMessage({ id: headertext });
    };

    const onKeyDownHandler = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter') {
            onClosePopup();
        }
    };

    const onKeyDownMainPopupHandler = (event: React.KeyboardEvent): void => {
        if (event.key === 'Escape') {
            isDismissable && onClosePopup();
        }
    };

    if (!show) {
        return null;
    }

    return (
        <FocusLock>
            <div
                className={`${popupStyles.popup} ${className} d-flex justify-content-center align-items-center overflow-auto`}
                onKeyDown={onKeyDownMainPopupHandler}
                role="dialog"
                aria-modal="true"
                aria-labelledby="headerPopupLabel"
                aria-describedby="innerPopup"
            >
                <div className={innerClasses}>
                    {shouldRenderHeader && (
                        <div id="headerPopup" className={headerClasses}>
                            {type && renderPopupHeaderIcon() && (
                                <div className={popupStyles.headerTitleIcon}>{renderPopupHeaderIcon()}</div>
                            )}
                            <div
                                className={`${popupStyles.headerTitleContent} text-truncate w-100`}
                                id="headerPopupLabel"
                            >
                                {headertext ? (
                                    <h2
                                        title={renderPopupHeaderText()}
                                        className={`${multilineHeader ? 'pre-line' : 'text-truncate'} my-0`}
                                    >
                                        {renderPopupHeaderText()}
                                    </h2>
                                ) : (
                                    htmlHeader || null
                                )}
                            </div>
                            {isDismissable && (
                                <Image
                                    internalRef={imageRef}
                                    role="button"
                                    className={popupStyles.modalClose}
                                    src={images.modalClose}
                                    alt="CLOSE"
                                    aria-label={intl.formatMessage({ id: 'CLOSE' })}
                                    onClick={onClosePopup}
                                    {...(!isTouchScreen() && { onKeyDown: onKeyDownHandler })}
                                />
                            )}
                        </div>
                    )}
                    <div
                        id="innerPopup"
                        ref={innerPopup}
                        className={`${popupStyles.popup_scroll} d-flex flex-column h-100`}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </FocusLock>
    );
};
